import React from 'react';
import { Box, Container, Typography, Button, IconButton, Chip } from '@mui/material';
import { ArrowLeft, UserPlus, Edit2, Globe, Lock, Users } from 'lucide-react';
import { Workspace, WorkspaceVisibility } from '../../types/common';
import './WorkspaceHeader.scss';

interface WorkspaceHeaderProps {
  workspace: Workspace;
  onBack: () => void;
  onEdit: () => void;
  onAddMember: () => void;
  isOwnerOrAdmin: boolean;
}

const WorkspaceHeader: React.FC<WorkspaceHeaderProps> = ({
  workspace,
  onBack,
  onEdit,
  onAddMember,
  isOwnerOrAdmin,
}) => {
  const getVisibilityIcon = (visibility: WorkspaceVisibility) => {
    switch (visibility) {
      case WorkspaceVisibility.OPEN:
      case WorkspaceVisibility.PUBLIC:
        return <Globe size={16} />;
      case WorkspaceVisibility.HIDDEN:
      case WorkspaceVisibility.PRIVATE:
        return <Lock size={16} />;
      case WorkspaceVisibility.CLOSED:
      case WorkspaceVisibility.TEAM:
        return <Users size={16} />;
      default:
        return <Users size={16} />;
    }
  };

  const getVisibilityColor = (visibility: WorkspaceVisibility) => {
    switch (visibility) {
      case WorkspaceVisibility.OPEN:
      case WorkspaceVisibility.PUBLIC:
        return 'success';
      case WorkspaceVisibility.HIDDEN:
      case WorkspaceVisibility.PRIVATE:
        return 'error';
      case WorkspaceVisibility.CLOSED:
      case WorkspaceVisibility.TEAM:
        return 'primary';
      default:
        return 'default';
    }
  };

  return (
    <Box className="workspace-header" sx={{ borderTop: `4px solid ${workspace.color}` }}>
      <Container maxWidth="xl">
        <Box className="workspace-header-content">
          <Box className="workspace-header-left">
            <IconButton onClick={onBack} sx={{ mr: 2 }}>
              <ArrowLeft size={20} />
            </IconButton>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Typography variant="h4" component="h1">
                  {workspace.name}
                </Typography>
                <Chip
                  icon={getVisibilityIcon(workspace.visibility)}
                  label={workspace.visibility}
                  size="small"
                  color={getVisibilityColor(workspace.visibility) as any}
                  variant="outlined"
                />
              </Box>
              {workspace.description && (
                <Typography variant="body2" color="text.secondary">
                  {workspace.description}
                </Typography>
              )}
            </Box>
          </Box>

          <Box className="workspace-header-actions">
            {isOwnerOrAdmin && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<UserPlus size={18} />}
                  onClick={onAddMember}
                >
                  Add Member
                </Button>
                <Button variant="outlined" startIcon={<Edit2 size={18} />} onClick={onEdit}>
                  Edit
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default WorkspaceHeader;
